import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DieselMatchDialog } from "@/components/DieselMatchDialog";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { usePaged, PageSize, Pager, ModuleTabs } from "@/components/Toolbar";
import { fmtNumber, fmtCurrency } from "@/lib/format";
import { RotateCw, FileSpreadsheet, Printer, Search, Pencil, Check } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";
import { SortHeader, useOwnerSort } from "@/components/SortHeader";
import { toast } from "sonner";
import { rowTone, mixTotalTone, mixIndexOf, MixChip, OwnerBadge, filterByLoadLocation } from "@/components/RowTone";

const dt = (s) => (s ? new Date(s).toLocaleDateString("en-GB") : "-");
const totalOf = (r) => (r.quantity || 0) * (r.company_rate || 0);
const commOf = (r) => (r.quantity || 0) * ((r.company_rate || 0) - (r.market_rate || 0));
const paidOf = (r) => (r.diesel || 0) + (r.bank_transfer || 0) + (r.shortage || 0);
const balanceOf = (r) => (r.quantity || 0) * (r.market_rate || 0) - paidOf(r);
const diffOf = (r) => (r.unloading_qty ? r.unloading_qty - (r.quantity || 0) : 0);

const EXPORT_COLS = [
  ["Bill No", (r) => r.bill_no], ["GR No", (r) => r.gr_no], ["GR Date", (r) => (r.gr_date || "").slice(0, 10)], ["Material Type", (r) => r.material_type],
  ["Truck", (r) => r.vehicle_number], ["Vehicle Type", (r) => r.vehicle_type], ["Owner", (r) => r.owner_name], ["Delivery No", (r) => r.delivery_number],
  ["Load Location", (r) => r.load_location], ["Delivery Location", (r) => r.delivery_location], ["Qty", (r) => r.quantity], ["Rate", (r) => r.company_rate],
  ["Market Rate", (r) => r.market_rate], ["Total", totalOf], ["Commission", commOf], ["Diesel", (r) => r.diesel], ["Cash", (r) => r.cash],
  ["Bank Transfer", (r) => r.bank_transfer], ["FASTag", (r) => r.fastag], ["Shortage", (r) => r.shortage], ["Fuel Qty (L)", (r) => r.fuel_qty],
  ["Unloading Amount", (r) => r.unloading_amount], ["Unloading Qty", (r) => r.unloading_qty], ["Balance", balanceOf], ["Mixed Load", (r) => (r.group_id ? "Yes" : "")],
];
const exportExcel = (rows) => {
  const name = rows[0]?.posted ? "Receive_Register" : "Acknowledgement";
  const esc = (v) => { const s = v === undefined || v === null ? "" : typeof v === "number" ? String(Math.round(v * 100) / 100) : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [["SL", ...EXPORT_COLS.map((c) => c[0])].join(","), ...rows.map((r, i) => [i + 1, ...EXPORT_COLS.map((c) => c[1](r))].map(esc).join(","))];
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${name}_${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(a.href);
};

const IconBtn = ({ children, ...p }) => (
  <button className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-accent" {...p}>{children}</button>
);

const Th = ({ children, r }) => <th className={`py-3 px-3 ${r ? "text-right" : "text-left"} font-semibold`}>{children}</th>;

const EDIT_SECTIONS = [
  { title: "Rates & Unloading", fields: [
    { k: "company_rate", l: "Company Rate (₹/Ton)" },
    { k: "market_rate", l: "Market Rate (₹/Ton)" },
    { k: "unloading_qty", l: "Unloading Qty (Tons)" },
  ] },
  { title: "Advances (₹)", fields: [
    { k: "diesel", l: "Diesel (Advance ₹)" },
    { k: "cash", l: "Cash (Advance ₹)" },
    { k: "bank_transfer", l: "Bank Transfer (Advance ₹)" },
    { k: "fastag", l: "FASTag (₹)" },
    { k: "fuel_qty", l: "Fuel Qty (Litres)" },
    { k: "unloading_amount", l: "Unloading Amount (₹)" },
  ] },
  { title: "Deductions", fields: [{ k: "shortage", l: "Shortage (₹)" }] },
];
const EDIT_FIELDS = EDIT_SECTIONS.flatMap((s) => s.fields);

const EditSection = ({ title, children }) => (
  <div className="rounded-xl border border-border/60 bg-muted/20 p-4 space-y-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-mono">{title}</div>
    {children}
  </div>
);

const InfoCell = ({ l, v, mono }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-sm text-muted-foreground">{l}</span>
    <div className={`h-10 px-3 rounded-md border border-border bg-muted/40 flex items-center text-sm truncate ${mono ? "font-mono" : ""}`}>{v || "-"}</div>
  </div>
);

function ChallanEditDialog({ challan, onClose, onSaved }) {
  const [f, setF] = useState(() => Object.fromEntries(EDIT_FIELDS.map(({ k }) => [k, challan[k] ?? 0])));
  const num = (k) => Number(f[k] || 0);
  const view = { ...challan, ...Object.fromEntries(EDIT_FIELDS.map(({ k }) => [k, num(k)])) };
  const bal = balanceOf(view);
  const save = async () => {
    try {
      await api.put(`/challans/${challan.id}`, { ...challan, ...Object.fromEntries(EDIT_FIELDS.map(({ k }) => [k, num(k)])) });
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="challan-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Receipt</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <EditSection title="Trip Details">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4" data-testid="edit-trip-details">
              <InfoCell l="GR Date" v={challan.gr_date ? challan.gr_date.slice(0, 10).split("-").reverse().join("/") : ""} />
              <InfoCell l="Vehicle Number" v={challan.vehicle_number} mono />
              <InfoCell l="Owner Name" v={challan.owner_name} />
              <InfoCell l="Material Type" v={challan.material_type} />
              <InfoCell l="Delivery Number / Shipment No" v={challan.delivery_number} mono />
              <InfoCell l="Quantity (Tons)" v={fmtNumber(challan.quantity)} mono />
              <InfoCell l="Load Location" v={challan.load_location} />
              <InfoCell l="Delivery Location" v={challan.delivery_location} />
              <InfoCell l="Bill No" v={challan.bill_no} mono />
            </div>
          </EditSection>
          {EDIT_SECTIONS.map((s) => (
            <EditSection key={s.title} title={s.title}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {s.fields.map(({ k, l }) => (
                  <div key={k} className="flex flex-col gap-1.5">
                    <label className="text-sm text-muted-foreground">{l}</label>
                    <Input type="number" className="h-10 font-mono" value={f[k]} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} data-testid={`edit-field-${k}`} />
                  </div>
                ))}
              </div>
            </EditSection>
          ))}
        </div>
        <div className="sticky bottom-0 -mx-6 -mb-6 mt-2 px-6 py-4 bg-card/95 backdrop-blur-md border-t border-border/60 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap text-sm">
            <span className="text-muted-foreground">Freight: <span className="font-mono font-semibold text-foreground">{fmtCurrency(totalOf(view))}</span></span>
            <span className="text-muted-foreground">Commission: <span className="font-mono font-semibold text-emerald-500">{fmtCurrency(commOf(view))}</span></span>
            <span className="text-muted-foreground">Balance: <span className={`font-mono font-bold ${bal < 0 ? "text-rose-500" : "text-emerald-500"}`} data-testid="edit-balance-preview">{bal >= 0 ? "+" : ""}{fmtNumber(bal)}</span></span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="edit-cancel">Cancel</Button>
            <Button onClick={save} data-testid="edit-save">Save Changes</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Receive() {
  const [tab, setTab] = useState("ack");
  const [refreshKey, setRefreshKey] = useState(0);
  const { selectedHub, hubs } = useApp();
  const isBulk = (hubs.find((h) => h.id === selectedHub)?.name || "").trim().toLowerCase() === "dalmia bulk";
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <h1 className="font-display text-2xl font-bold">Receive</h1>
        {isBulk && <DieselMatchDialog hubId={selectedHub} onApplied={() => setRefreshKey((k) => k + 1)} />}
      </div>
      <ModuleTabs tabs={[{ id: "ack", label: "Acknowledgement" }, { id: "register", label: "Receive Register" }]} active={tab} onChange={setTab} />
      {tab === "ack" ? <Acknowledgement key={refreshKey} /> : <ReceiveRegister key={refreshKey} />}
    </div>
  );
}

function Filters({ q, setQ, from, setFrom, to, setTo, onRefresh, pageSize, setPageSize, loc, setLoc, locs, dloc, setDloc, dlocs, onExport }) {
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9 w-56" placeholder="Search delivery no / owner" value={q} onChange={(e) => setQ(e.target.value)} data-testid="receive-search" />
      </div>
      <DatePicker value={from} onChange={setFrom} placeholder="From date" testid="receive-from-date" />
      <DatePicker value={to} onChange={setTo} placeholder="To date" testid="receive-to-date" />
      <Select value={loc} onValueChange={setLoc}>
        <SelectTrigger className="w-48" data-testid="receive-load-location-filter"><SelectValue placeholder="Load Location" /></SelectTrigger>
        <SelectContent><SelectItem value="all">All Load Locations</SelectItem>{locs.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={dloc} onValueChange={setDloc}>
        <SelectTrigger className="w-48" data-testid="receive-delivery-location-filter"><SelectValue placeholder="Delivery Location" /></SelectTrigger>
        <SelectContent><SelectItem value="all">All Delivery Locations</SelectItem>{dlocs.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <IconBtn onClick={onRefresh} data-testid="receive-refresh"><RotateCw className="h-4 w-4" /></IconBtn>
      <div className="ml-auto flex items-center gap-2">
        <IconBtn onClick={onExport} title="Download Excel" data-testid="receive-export"><FileSpreadsheet className="h-4 w-4" /></IconBtn>
        <IconBtn onClick={() => window.print()} data-testid="receive-print"><Printer className="h-4 w-4" /></IconBtn>
        <PageSize value={pageSize} onChange={setPageSize} />
      </div>
    </div>
  );
}

function useChallanRows(posted) {
  const { selectedHub } = useApp();
  const [allRows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loc, setLoc] = useState("all");
  const locs = useMemo(() => [...new Set(allRows.map((r) => (r.load_location || "").trim()).filter(Boolean))].sort(), [allRows]);
  const [dloc, setDloc] = useState("all");
  const dlocs = useMemo(() => [...new Set(allRows.map((r) => (r.delivery_location || "").trim()).filter(Boolean))].sort(), [allRows]);
  const rows = useMemo(() => filterByLoadLocation(filterByLoadLocation(allRows, loc), dloc, "delivery_location"), [allRows, loc, dloc]);
  const load = useCallback(async () => {
    const params = { hub: selectedHub, posted };
    if (q) params.q = q;
    let data = (await api.get("/challans", { params })).data;
    if (from) data = data.filter((d) => d.gr_date >= from);
    if (to) data = data.filter((d) => d.gr_date <= to);
    if (!posted) data.sort((a, b) => (b.ageing || 0) - (a.ageing || 0));
    setRows(data);
  }, [selectedHub, q, from, to, posted]);
  useEffect(() => { load(); }, [load]);
  return { rows, q, setQ, from, setFrom, to, setTo, load, loc, setLoc, locs, dloc, setDloc, dlocs };
}

function useGrouping(rows) {
  const { sorted: osRows, dir: osDir, toggle: osToggle } = useOwnerSort(rows);
  const serialByRow = {}; const firstOfUnit = {};
  { const seen = {}; let n = 0; osRows.forEach((r) => { const k = r.group_id || r.id; if (!(k in seen)) { seen[k] = ++n; firstOfUnit[r.id] = true; } serialByRow[r.id] = seen[k]; }); }
  const mixGroupRows = {};
  rows.forEach((r) => { if (r.group_id) (mixGroupRows[r.group_id] = mixGroupRows[r.group_id] || []).push(r); });
  const mixLastRow = {};
  osRows.forEach((r) => { if (r.group_id && (mixGroupRows[r.group_id] || []).length > 1) mixLastRow[r.group_id] = r.id; });
  const mixIdx = mixIndexOf(osRows);
  return { osRows, osDir, osToggle, serialByRow, firstOfUnit, mixGroupRows, mixLastRow, mixIdx };
}

const gsum = (g, fn) => g.reduce((s, x) => s + fn(x), 0);

function HeadCells({ isDalmia, isDalmiaBulk, ack, showAction, osDir, osToggle }) {
  return (
    <tr>
      <Th>Sl</Th>{!ack && <Th>Bill No</Th>}{ack && <Th>Ageing</Th>}<Th>GR No</Th><Th>GR Date</Th><Th>Material Type</Th><Th>Truck</Th><Th>Vehicle Type</Th>
      <SortHeader label="Owner" dir={osDir} onClick={osToggle} className="text-left font-semibold" />
      <Th>Delivery No</Th><Th>Load Location</Th><Th>Delivery Location</Th>
      <Th r>Qty</Th><Th r>Rate</Th><Th r>Market Rate</Th><Th r>Total</Th>
      {isDalmiaBulk && <><Th r>Unloading Qty</Th><Th r>Difference</Th></>}
      {!isDalmia && <Th r>Comm.</Th>}
      <Th r>Diesel</Th><Th r>Cash</Th><Th r>Bank</Th><Th r>FASTag</Th><Th r>Shortage</Th><Th r>Balance</Th>
      {(isDalmia || isDalmiaBulk) && <Th r>Fuel Qty</Th>}{isDalmia && <Th r>Unloading</Th>}
      {showAction && <Th>Action</Th>}
    </tr>
  );
}

function RowCells({ r, ack, isDalmia, isDalmiaBulk, showAction, serialByRow, firstOfUnit, onEdit, onPost, ageColor, mixCount, mixI }) {
  const bal = balanceOf(r);
  return (
    <>
      <td className="py-2 px-3 text-muted-foreground">{firstOfUnit[r.id] ? <span className="font-semibold text-foreground/80">{serialByRow[r.id]}</span> : <span className="pl-1 opacity-60">↳</span>}</td>
      {!ack && <td className="py-2 px-3 font-mono text-xs text-primary">{r.bill_no && r.bill_no !== "billed" ? r.bill_no : (r.bill_no === "billed" ? "Billed" : "-")}</td>}
      {ack && <td className="py-2 px-3 text-center"><span className={`inline-flex min-w-[40px] justify-center px-2 py-1 rounded text-white text-xs font-bold ${ageColor(Math.max(0, r.ageing))}`}>{Math.max(0, r.ageing)}</span></td>}
      <td className="py-2 px-3 font-mono text-xs">{r.gr_no || "-"}</td>
      <td className="py-2 px-3">{dt(r.gr_date)}</td>
      <td className="py-2 px-3">{r.material_type || "-"}{mixI !== undefined && firstOfUnit[r.id] ? <MixChip count={mixCount} mixIdx={mixI} /> : null}</td>
      <td className="py-2 px-3 font-mono">{r.vehicle_number}</td>
      <td className="py-2 px-3">{r.vehicle_type || "-"}</td>
      <td className="py-2 px-3"><OwnerBadge name={r.owner_name} /></td>
      <td className="py-2 px-3 font-mono text-xs">{r.delivery_number}</td>
      <td className="py-2 px-3 max-w-[130px] truncate" title={r.load_location || ""}>{r.load_location || "-"}</td>
      <td className="py-2 px-3 max-w-[140px] truncate" title={r.delivery_location || ""}>{r.delivery_location || "-"}</td>
      <td className="py-2 px-3 text-right font-mono">{fmtNumber(r.quantity)}</td>
      <td className="py-2 px-3 text-right font-mono">{fmtNumber(r.company_rate)}</td>
      <td className="py-2 px-3 text-right font-mono">{fmtNumber(r.market_rate)}</td>
      <td className="py-2 px-3 text-right font-mono font-semibold">{fmtNumber(totalOf(r))}</td>
      {isDalmiaBulk && <>
        <td className="py-2 px-3 text-right font-mono">{fmtNumber(r.unloading_qty || 0)}</td>
        <td className={`py-2 px-3 text-right font-mono font-semibold ${diffOf(r) < 0 ? "text-rose-500" : ""}`} data-testid={`diff-${r.id}`}>{fmtNumber(diffOf(r))}</td>
      </>}
      {!isDalmia && <td className="py-2 px-3 text-right font-mono text-primary">{fmtNumber(commOf(r))}</td>}
      <td className="py-2 px-3 text-right font-mono">{fmtNumber(r.diesel)}</td>
      <td className="py-2 px-3 text-right font-mono">{fmtNumber(r.cash)}</td>
      <td className="py-2 px-3 text-right font-mono">{fmtNumber(r.bank_transfer)}</td>
      <td className="py-2 px-3 text-right font-mono">{fmtNumber(r.fastag)}</td>
      <td className={`py-2 px-3 text-right font-mono ${r.shortage ? "text-rose-500 font-semibold" : ""}`}>{fmtNumber(r.shortage)}</td>
      <td className={`py-2 px-3 text-right font-mono font-semibold ${bal < 0 ? "text-rose-500" : "text-emerald-500"}`}>{bal >= 0 ? "+" : ""}{fmtNumber(bal)}</td>
      {(isDalmia || isDalmiaBulk) && <td className="py-2 px-3 text-right font-mono" data-testid={`fuel-qty-${r.id}`}>{fmtNumber(r.fuel_qty || 0)}</td>}
      {isDalmia && <td className="py-2 px-3 text-right font-mono">{fmtNumber(r.unloading_amount || 0)}</td>}
      {showAction && (
        <td className="py-2 px-3">
          <div className="flex items-center justify-center gap-1.5">
            <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs gap-1.5 font-medium" onClick={() => onEdit(r)} data-testid={`${ack ? "ack" : "register"}-edit-${r.id}`} title="Edit amounts"><Pencil className="h-3.5 w-3.5" />Edit</Button>
            {ack && <Button size="sm" className="h-7 px-2.5 text-xs gap-1.5 font-medium bg-emerald-600 hover:bg-emerald-600/90 text-white" onClick={() => onPost(r)} data-testid={`ack-post-${r.id}`} title="Post to Receive Register"><Check className="h-3.5 w-3.5" />Post</Button>}
          </div>
        </td>
      )}
    </>
  );
}

function MixTotalRow({ g, ack, isDalmia, isDalmiaBulk, showAction, gid, mixI }) {
  const gbal = gsum(g, balanceOf);
  return (
    <tr className={mixTotalTone(mixI)} data-testid={`${ack ? "ack" : "register"}-mix-total-${gid}`}>
      <td colSpan={11} className="py-2 px-3 text-xs uppercase tracking-wide">Σ Mixed Load Total · {g.length} deliveries</td>
      <td className="py-2 px-3 text-right font-mono font-bold">{fmtNumber(gsum(g, (x) => x.quantity || 0))}</td>
      <td></td><td></td>
      <td className="py-2 px-3 text-right font-mono font-bold">{fmtNumber(gsum(g, totalOf))}</td>
      {isDalmiaBulk && <>
        <td className="py-2 px-3 text-right font-mono font-bold">{fmtNumber(gsum(g, (x) => x.unloading_qty || 0))}</td>
        <td className={`py-2 px-3 text-right font-mono font-bold ${gsum(g, diffOf) < 0 ? "text-rose-500" : ""}`}>{fmtNumber(gsum(g, diffOf))}</td>
      </>}
      {!isDalmia && <td className="py-2 px-3 text-right font-mono font-bold text-primary">{fmtNumber(gsum(g, commOf))}</td>}
      <td className="py-2 px-3 text-right font-mono font-bold">{fmtNumber(gsum(g, (x) => x.diesel || 0))}</td>
      <td className="py-2 px-3 text-right font-mono font-bold">{fmtNumber(gsum(g, (x) => x.cash || 0))}</td>
      <td className="py-2 px-3 text-right font-mono font-bold">{fmtNumber(gsum(g, (x) => x.bank_transfer || 0))}</td>
      <td className="py-2 px-3 text-right font-mono font-bold">{fmtNumber(gsum(g, (x) => x.fastag || 0))}</td>
      <td className="py-2 px-3 text-right font-mono font-bold">{fmtNumber(gsum(g, (x) => x.shortage || 0))}</td>
      <td className={`py-2 px-3 text-right font-mono font-bold ${gbal < 0 ? "text-rose-500" : "text-emerald-500"}`}>{gbal >= 0 ? "+" : ""}{fmtNumber(gbal)}</td>
      {(isDalmia || isDalmiaBulk) && <td className="py-2 px-3 text-right font-mono font-bold">{fmtNumber(gsum(g, (x) => x.fuel_qty || 0))}</td>}
      {isDalmia && <td className="py-2 px-3 text-right font-mono font-bold">{fmtNumber(gsum(g, (x) => x.unloading_amount || 0))}</td>}
      {showAction && <td></td>}
    </tr>
  );
}

const colCount = (ack, isDalmia, isDalmiaBulk, showAction) =>
  22 + (isDalmia ? 1 : 0) + (isDalmiaBulk ? 3 : 0) + (showAction ? 1 : 0);

function Acknowledgement() {
  const { selectedHub, hubs } = useApp();
  const hubName = (hubs.find((h) => h.id === selectedHub)?.name || "").trim().toLowerCase();
  const isDalmia = ["dalmia", "dalmia dedicated"].includes(hubName);
  const isDalmiaBulk = hubName === "dalmia bulk";
  const { rows, q, setQ, from, setFrom, to, setTo, load, loc, setLoc, locs, dloc, setDloc, dlocs } = useChallanRows(false);
  const { osRows, osDir, osToggle, serialByRow, firstOfUnit, mixGroupRows, mixLastRow, mixIdx } = useGrouping(rows);
  const { page, setPage, pageSize, setPageSize, totalPages, pageRows } = usePaged(osRows);
  const [editing, setEditing] = useState(null);
  const post = async (r) => { await api.post(`/challans/${r.id}/post`); toast.success(`Posted ${r.vehicle_number}`); load(); };
  const ageColor = (a) => (a > 365 ? "bg-rose-600" : a > 60 ? "bg-orange-500" : "bg-emerald-600");
  const nCols = colCount(true, isDalmia, isDalmiaBulk, true);

  return (
    <div>
      <Filters q={q} setQ={setQ} from={from} setFrom={setFrom} to={to} setTo={setTo} onRefresh={load} pageSize={pageSize} setPageSize={setPageSize} loc={loc} setLoc={setLoc} locs={locs} dloc={dloc} setDloc={setDloc} dlocs={dlocs} onExport={() => exportExcel(osRows)} />
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap" data-testid="ack-table">
          <thead className="bg-muted/60 text-muted-foreground text-xs uppercase">
            <HeadCells isDalmia={isDalmia} isDalmiaBulk={isDalmiaBulk} ack showAction osDir={osDir} osToggle={osToggle} />
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <React.Fragment key={r.id}>
                <tr className={`border-t border-border ${rowTone(mixIdx[r.group_id], serialByRow[r.id])} ${r.group_id && firstOfUnit[r.id] ? "row-mix-start" : ""}`} data-testid={`ack-row-${r.id}`}>
                  <RowCells r={r} ack isDalmia={isDalmia} isDalmiaBulk={isDalmiaBulk} showAction serialByRow={serialByRow} firstOfUnit={firstOfUnit} onEdit={setEditing} onPost={post} ageColor={ageColor} mixCount={r.group_id ? mixGroupRows[r.group_id]?.length : 0} mixI={mixIdx[r.group_id]} />
                </tr>
                {r.group_id && mixLastRow[r.group_id] === r.id && <MixTotalRow g={mixGroupRows[r.group_id]} ack isDalmia={isDalmia} isDalmiaBulk={isDalmiaBulk} showAction gid={r.group_id} mixI={mixIdx[r.group_id]} />}
              </React.Fragment>
            ))}
            {pageRows.length === 0 && <tr><td colSpan={nCols} className="py-16 text-center text-muted-foreground">Nothing pending acknowledgement</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} setPage={setPage} />
      {editing && <ChallanEditDialog challan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ReceiveRegister() {
  const { selectedHub, hubs } = useApp();
  const { isAdmin } = useAuth();
  const hubName = (hubs.find((h) => h.id === selectedHub)?.name || "").trim().toLowerCase();
  const isDalmia = ["dalmia", "dalmia dedicated"].includes(hubName);
  const isDalmiaBulk = hubName === "dalmia bulk";
  const { rows, q, setQ, from, setFrom, to, setTo, load, loc, setLoc, locs, dloc, setDloc, dlocs } = useChallanRows(true);
  const { osRows, osDir, osToggle, serialByRow, firstOfUnit, mixGroupRows, mixLastRow, mixIdx } = useGrouping(rows);
  const { page, setPage, pageSize, setPageSize, totalPages, pageRows } = usePaged(osRows);
  const [editing, setEditing] = useState(null);
  const sum = (fn) => rows.reduce((s, r) => s + fn(r), 0);
  const nCols = colCount(false, isDalmia, isDalmiaBulk, isAdmin);

  return (
    <div>
      <Filters q={q} setQ={setQ} from={from} setFrom={setFrom} to={to} setTo={setTo} onRefresh={load} pageSize={pageSize} setPageSize={setPageSize} loc={loc} setLoc={setLoc} locs={locs} dloc={dloc} setDloc={setDloc} dlocs={dlocs} onExport={() => exportExcel(osRows)} />
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap" data-testid="register-table">
          <thead className="bg-muted/60 text-muted-foreground text-xs uppercase">
            <HeadCells isDalmia={isDalmia} isDalmiaBulk={isDalmiaBulk} ack={false} showAction={isAdmin} osDir={osDir} osToggle={osToggle} />
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <React.Fragment key={r.id}>
                <tr className={`border-t border-border ${rowTone(mixIdx[r.group_id], serialByRow[r.id])} ${r.group_id && firstOfUnit[r.id] ? "row-mix-start" : ""}`} data-testid={`register-row-${r.id}`}>
                  <RowCells r={r} ack={false} isDalmia={isDalmia} isDalmiaBulk={isDalmiaBulk} showAction={isAdmin} serialByRow={serialByRow} firstOfUnit={firstOfUnit} onEdit={setEditing} mixCount={r.group_id ? mixGroupRows[r.group_id]?.length : 0} mixI={mixIdx[r.group_id]} />
                </tr>
                {r.group_id && mixLastRow[r.group_id] === r.id && <MixTotalRow g={mixGroupRows[r.group_id]} ack={false} isDalmia={isDalmia} isDalmiaBulk={isDalmiaBulk} showAction={isAdmin} gid={r.group_id} mixI={mixIdx[r.group_id]} />}
              </React.Fragment>
            ))}
            {pageRows.length === 0 && <tr><td colSpan={nCols} className="py-16 text-center text-muted-foreground">No posted receipts</td></tr>}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border bg-muted/40 font-semibold" data-testid="register-total">
              <td className="py-3 px-3" colSpan={11}>Total ({rows.length})</td>
              <td className="py-3 px-3 text-right font-mono">{fmtNumber(sum((r) => r.quantity || 0))}</td>
              <td></td><td></td>
              <td className="py-3 px-3 text-right font-mono">{fmtNumber(sum(totalOf))}</td>
              {isDalmiaBulk && <>
                <td className="py-3 px-3 text-right font-mono">{fmtNumber(sum((r) => r.unloading_qty || 0))}</td>
                <td className={`py-3 px-3 text-right font-mono ${sum(diffOf) < 0 ? "text-rose-500" : ""}`}>{fmtNumber(sum(diffOf))}</td>
              </>}
              {!isDalmia && <td className="py-3 px-3 text-right font-mono text-primary">{fmtNumber(sum(commOf))}</td>}
              <td className="py-3 px-3 text-right font-mono">{fmtNumber(sum((r) => r.diesel || 0))}</td>
              <td className="py-3 px-3 text-right font-mono">{fmtNumber(sum((r) => r.cash || 0))}</td>
              <td className="py-3 px-3 text-right font-mono">{fmtNumber(sum((r) => r.bank_transfer || 0))}</td>
              <td className="py-3 px-3 text-right font-mono">{fmtNumber(sum((r) => r.fastag || 0))}</td>
              <td className="py-3 px-3 text-right font-mono">{fmtNumber(sum((r) => r.shortage || 0))}</td>
              <td className="py-3 px-3 text-right font-mono text-emerald-500">{fmtNumber(sum(balanceOf))}</td>
              {(isDalmia || isDalmiaBulk) && <td className="py-3 px-3 text-right font-mono">{fmtNumber(sum((r) => r.fuel_qty || 0))}</td>}{isDalmia && <td className="py-3 px-3 text-right font-mono">{fmtNumber(sum((r) => r.unloading_amount || 0))}</td>}
              {isAdmin && <td></td>}
            </tr>
          </tfoot>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} setPage={setPage} />
      {editing && <ChallanEditDialog challan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
